import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { type IncomingInvoiceSchema, incomingInvoiceSchema } from "@msuite/utils/schemas";
import type { IncomingInvoiceDetail } from "@msuite/utils/types";
import { FormProvider, useForm } from "react-hook-form";
import { ModalLoadFailed } from "@/components/modals";
import { IncomingInvoiceForm } from "@/forms/incoming-invoice-form";
import { client } from "@/hono";

/** Props Interface */
interface EditIncomingInvoiceModalProps extends ModalProps {
	incomingInvoiceId: string;
}

/**
 * The form's defaults, picked off the stored invoice. Not run through the schema: a stored row
 * must always load, and the resolver makes the person close any gap before saving.
 */
function toFormValues(invoice: IncomingInvoiceDetail): IncomingInvoiceSchema {
	return {
		kind: invoice.kind,
		number: invoice.number,
		invoiceDate: invoice.invoiceDate,
		dueDate: invoice.dueDate,
		currency: invoice.currency,
		workspaceId: invoice.workspaceId,
		contactId: invoice.contactId,
		reportNumber: invoice.reportNumber,
		netAmount: invoice.netAmount,
		vatRate: invoice.vatRate,
		vatAmount: invoice.vatAmount,
		grossAmount: invoice.grossAmount,
		skontoRate: invoice.skontoRate,
		skontoAmount: invoice.skontoAmount,
		skontoDeadline: invoice.skontoDeadline,
		issuerName: invoice.issuerName,
		issuerStreet: invoice.issuerStreet,
		issuerHouseNumber: invoice.issuerHouseNumber,
		issuerZipCode: invoice.issuerZipCode,
		issuerCity: invoice.issuerCity,
		issuerCountry: invoice.issuerCountry,
		issuerVatId: invoice.issuerVatId,
		issuerTaxNumber: invoice.issuerTaxNumber,
		issuerEmail: invoice.issuerEmail,
		issuerPhone: invoice.issuerPhone,
	};
}

export function EditIncomingInvoiceModal({
	isOpen,
	onClose,
	incomingInvoiceId,
}: EditIncomingInvoiceModalProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const qc = useQueryClient();
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		const response = await client.incoming_invoices[":incomingInvoiceId"].$get({
			param: { incomingInvoiceId },
		});
		const data = await responseHandler(response);
		return toFormValues(data.incomingInvoice as IncomingInvoiceDetail);
	});
	const methods = useForm<IncomingInvoiceSchema>({
		defaultValues,
		resolver: resolver(incomingInvoiceSchema),
	});

	/** Functions */
	async function handleOnSubmit(json: IncomingInvoiceSchema) {
		const response = await client.incoming_invoices[":incomingInvoiceId"].$patch({
			param: { incomingInvoiceId },
			json,
		});
		await responseHandler(response);
		await qc.invalidateQueries({
			predicate: invalidate(["incoming-invoices", "incoming-invoice-approvals-mine"]),
		});
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={t("incoming_invoices.edit_invoice")}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading && !loadFailed}
				width="52rem"
			>
				{loadFailed ? (
					<ModalLoadFailed onClose={onClose} />
				) : (
					<>
						<Modal.Body>
							<IncomingInvoiceForm />
						</Modal.Body>
						<Modal.FooterForm />
					</>
				)}
			</Modal>
		</FormProvider>
	);
}
